import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BellIcon,
  UserGroupIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { adminApi } from '../services/api';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

export default function Dashboard() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboardStats'],
    queryFn: () => adminApi.getDashboardStats(),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const dashboardData = stats?.data || {
    notifications: { total: 0, sent: 0, failed: 0, read: 0, avg_opens: 0 },
    users: { total: 0, new_week: 0, new_month: 0 },
    interactions: { viewed: 0, clicked: 0, quiz_answered: 0 },
  };

  // Prepare chart data
  const notificationStatusData = [
    { name: 'Sent', value: dashboardData.notifications.sent, color: '#3B82F6' },
    { name: 'Read', value: dashboardData.notifications.read, color: '#10B981' },
    { name: 'Failed', value: dashboardData.notifications.failed, color: '#EF4444' },
  ];

  const interactionData = [
    { name: 'Views', count: dashboardData.interactions.viewed },
    { name: 'Clicks', count: dashboardData.interactions.clicked },
    { name: 'Quiz Answers', count: dashboardData.interactions.quiz_answered },
  ];

  const statCards = [
    {
      title: 'Total Users',
      value: dashboardData.users.total,
      icon: UserGroupIcon,
      color: 'from-blue-500 to-blue-600',
      change: `+${dashboardData.users.new_week} this week`,
    },
    {
      title: 'Notifications Sent',
      value: dashboardData.notifications.total,
      icon: BellIcon,
      color: 'from-purple-500 to-purple-600',
      change: `${Math.round((dashboardData.notifications.read / dashboardData.notifications.total) * 100 || 0)}% read rate`,
    },
    {
      title: 'Avg. Opens',
      value: dashboardData.notifications.avg_opens.toFixed(1),
      icon: ChartBarIcon,
      color: 'from-green-500 to-green-600',
      change: 'Per notification',
    },
    {
      title: 'Active Today',
      value: dashboardData.users.new_week,
      icon: CheckCircleIcon,
      color: 'from-orange-500 to-orange-600',
      change: 'Users engaged',
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="inline-flex items-center justify-center w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-6 p-6">
      {/* Page header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Monitor your notification system performance
        </p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => (
          <div
            key={stat.title}
            className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700"
          >
            <div className={`absolute inset-0 bg-gradient-to-r ${stat.color} opacity-5`} />
            <div className="relative p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {stat.title}
                  </p>
                  <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                    {stat.value.toLocaleString()}
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {stat.change}
                  </p>
                </div>
                <div className={`p-3 bg-gradient-to-r ${stat.color} rounded-lg`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Notification Status Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Notification Status
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={notificationStatusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {notificationStatusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* User Interactions Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            User Interactions
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={interactionData}>
              <CartesianGrid strokeDasharray="3 3" className="dark:opacity-20" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#8B5CF6" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Recent Notifications
          </h3>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {[1, 2, 3].map((i) => (
            <div key={i} className="px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <BellIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Welcome Message
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Sent to all users • 2 hours ago
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                    85% read
                  </span>
                  <ClockIcon className="w-4 h-4 text-gray-400" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      </div>
    </div>
  );
}